import {MultiSelect} from '@mantine/core';
import {useForm, zodResolver} from '@mantine/form';
import {useDisclosure} from '@mantine/hooks';
import type {AppError} from '@packages/utils';
import {useRouter} from 'next/router';
import {useState} from 'react';
import {COMMUNITY} from '../../../../config/constants/routes';
import {
  DEFAULT_NICKNAME,
  TAG_ID_FOLLOWED,
} from '../../../../core/entities/community/constants';
import {
  type CommunityTag,
  PostCommunityTopicFormInput,
} from '../../../../core/entities/community/schema';
import {
  useGetCommunityTagList,
  usePostCommunityTopic,
} from '../../../../core/repositories/communityRepository';
import {queryClient} from '../../../../lib/react-query';
import {
  showErrorSnackBar,
  showSuccessSnackBar,
} from '../../../../shared/components';
import {ConfirmTopicFormModal} from './Presenter/ConfirmTopicFormModal';

type Props = {
  communityNickname: string;
};

type CastedError = {message: AppError};

export const PostTopicForm: React.FC<Props> = ({communityNickname}) => {
  const router = useRouter();
  const [opened, {open, close}] = useDisclosure(false);
  const [isPostLoading, setIsPostLoading] = useState(false);

  const {data: tagList} = useGetCommunityTagList({
    isPopular: false,
  });

  const form = useForm<PostCommunityTopicFormInput>({
    initialValues: {
      nickname: communityNickname ?? '',
      title: '',
      body: '',
      tags: [],
    },
    validate: zodResolver(PostCommunityTopicFormInput),
  });

  const onSubmit = () => {
    open();
  };

  const onError = () => {
    form.setDirty({
      nickname: true,
      title: true,
      body: true,
      tags: true,
    });
  };

  const postTopic = usePostCommunityTopic({
    async onSuccessFnc() {
      await queryClient.invalidateQueries({
        queryKey: ['getInfiniteCommunityTopicList'],
      });
      showSuccessSnackBar({title: '送信しました', message: 'done'});
      form.reset();

      // トピック投稿後、自動的にフォローされるのでフォロー中のトピック一覧に遷移させる
      // TODO: トピック投稿エンドポイントの返り値でトピックIDを返すように変更する
      return router.push(`${COMMUNITY}?tag=${TAG_ID_FOLLOWED}`);
    },
    onMutateFnc() {
      setIsPostLoading(true);
      if (form.values.nickname === '') {
        form.values.nickname = DEFAULT_NICKNAME;
      }
    },
    onErrorFnc(error: unknown) {
      const castedError = error as CastedError;
      showErrorSnackBar({
        isNetworkError: castedError.message === 'NetworkError',
      });
    },
    onSettledFnc() {
      setIsPostLoading(false);
      close();
    },
  });

  const postTopicFunc = async () => {
    const communityTagObjList = convertToCommunityTagObjList(form.values.tags);
    const tags = communityTagObjList.map((tag) => tag.id);
    const hashtags = communityTagObjList.map((tag) => tag.name);

    postTopic.mutate({
      nickname: form.values.nickname,
      title: form.values.title,
      body: form.values.body,
      tags,
      hashtags,
    });
  };

  const convertToCommunityTagObjList = (
    selectedTags: string[],
  ): CommunityTag[] => {
    const selectedTagObjList: CommunityTag[] = [];
    if (!tagList?.ok) {
      return selectedTagObjList;
    }

    for (const tag of selectedTags) {
      const tagObj = tagList.value.communityTags.find(
        (t) => t.id.toString() === tag,
      );
      if (tagObj) {
        selectedTagObjList.push(tagObj);
      }
    }

    return selectedTagObjList;
  };

  return (
    <div className="bg-white p-16 shadow-modal rounded-12 mt-16">
      <p className="text-base font-bold text-black-1 leading">
        知りたいことを投稿してみましょう
      </p>
      <form onSubmit={form.onSubmit(onSubmit, onError)} className="mt-12">
        <div>
          <label className="text-base font-bold text-black-1 leading block mb-8">
            ニックネーム
          </label>
          <input
            id="nickname"
            {...form.getInputProps('nickname')}
            placeholder="ニックネーム"
            className="border-[1px] border-line border-collapse p-8 w-full rounded-[8px] text-base"
            autoComplete="off"
            type="text"
          />
          {form.isDirty('nickname') && form.errors.nickname && (
            <p className="text-notification-1 text-sm leading mt-8">
              {form.errors.nickname}
            </p>
          )}
          <div className="mt-8">
            <p className="text-base text-black-1 leading">
              * #任意の言葉 で固定の名前が使えます。
            </p>
            <p className="text-base text-black-1 leading">
              * 空白の場合「{DEFAULT_NICKNAME}」として投稿されます。
            </p>
          </div>
        </div>
        <div className="pt-16">
          <div className="flex flex-wrap mb-8">
            <label className="text-base font-bold text-black-1 leading block">
              タイトル
            </label>
            <span className="block bg-notification-1 text-white text-xs py-[2px] px-4 rounded-full leading ml-4">
              必須
            </span>
          </div>
          <input
            id="title"
            {...form.getInputProps('title')}
            placeholder="【外コン】夏インターンの戦略的撤退はあり？"
            className="border-[1px] border-line border-collapse p-8 w-full rounded-[8px] text-base"
            autoComplete="off"
            type="text"
          />
          {form.isDirty('title') && form.errors.title && (
            <p className="text-notification-1 text-sm leading mt-8">
              {form.errors.title}
            </p>
          )}
        </div>
        <div className="pt-16">
          <div className="flex flex-wrap mb-8">
            <label className="text-base font-bold text-black-1 leading block">
              投稿内容
            </label>
            <span className="block bg-notification-1 text-white text-xs py-[2px] px-4 rounded-full leading ml-4">
              必須
            </span>
          </div>
          <textarea
            id="body"
            {...form.getInputProps('body')}
            placeholder={
              '夏の外コンインターンを、戦略的撤退するのはありでしょうか。\n\n2018卒の先輩から、戦略コンサルは夏に落ちると二度と受けられない、もしくは書類落ちすることが多いから、例えば判断推理やGMATなどの筆記試験、以降のケース面接にまだ自信がないならば、夏は見送って、オータム、ウィンターに出せばよいと言われました。\n夏しかインターンをやっていないところが存在するかはわかりませんが、やはり枠の大きさを考えても、まだ準備段階ならば秋や冬に見越すのもありなのでしょうか。'
            }
            rows={8}
            className="border-[1px] border-line border-collapse p-8 w-full rounded-[8px] text-base"
          />
          {form.isDirty('body') && form.errors.body && (
            <p className="text-notification-1 text-sm leading mt-8">
              {form.errors.body}
            </p>
          )}
        </div>
        <div className="pt-16">
          <label className="text-base font-bold text-black-1 leading block mb-8">
            タグ
          </label>
          <MultiSelect
            id="tags"
            {...form.getInputProps('tags')}
            placeholder="タグ"
            className="text-base"
            data={convertToSelectBoxOptions(
              tagList?.ok ? tagList.value.communityTags : [],
            )}
            searchable
            nothingFound="Nothing found"
            radius={8}
          />
        </div>
        <div className="pt-16 flex justify-end">
          <button
            type="submit"
            className={`text-base font-bold bg-black-3  ${Object.keys(form.errors).length > 0 ? 'text-black-1' : 'text-white'} py-8 px-12 rounded-full ${Object.keys(form.errors).length > 0 ? 'bg-disabled-1' : 'bg-primary-1'}`}
            disabled={Object.keys(form.errors).length > 0}
          >
            確認画面へ
          </button>
        </div>
      </form>
      <ConfirmTopicFormModal
        nickname={form.values.nickname}
        title={form.values.title}
        body={form.values.body}
        tags={convertToCommunityTagObjList(form.values.tags)}
        opened={opened}
        close={close}
        postTopicFunc={postTopicFunc}
        isPostLoading={isPostLoading}
      />
    </div>
  );
};

type SelectBoxOption = {value: string; label: string};
function convertToSelectBoxOptions(tags: CommunityTag[]): SelectBoxOption[] {
  return tags.map((t) => {
    return {
      value: t.id.toString(),
      label: t.name,
    };
  });
}
